import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { KeyRound, Loader, Mail, Pencil, RefreshCw, Shield, Star, User, UserPlus } from 'lucide-react';
import { Icon } from './Icon';
import LoadFailure from './LoadFailure';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';
import { useToast } from '../toastContext';

export default function UsersPanel({ axiosAuth, currentUser, theme, BACKEND_URL, videoList = [] }) {
  const { pushToast } = useToast();
  const [users, setUsers]           = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userSubjects, setUserSubjects]     = useState({});  // { [userId]: [{project_root, role_override, allowed_parts}] }
  const [subjectSaving, setSubjectSaving]   = useState({});
  const [subjectError, setSubjectError]     = useState({});
  const [toggleError, setToggleError]       = useState({});  // { [userId]: message }

  // Derive unique project roots + available tapes from videoList
  const { allRoots, rootTapes } = React.useMemo(() => {
    const seen = new Set();
    const roots = [];
    const tapeMap = {};  // root → Set of tape numbers
    (videoList || []).forEach(v => {
      const r = v.replace(/[_.]?(sv|en)$/i,'').replace(/[_.]?(?:tape|part|pt|ep|episode)[._\s-]*\d+$/i,'').replace(/[._\s-]+\d+$/,'').trim();
      if (r && !seen.has(r)) { seen.add(r); roots.push(r); }
      if (r) {
        if (!tapeMap[r]) tapeMap[r] = new Set();
        const tapeMatch = v.match(/(?:tape|part|pt|ep|episode)[._\s-]*(\d+)/i);
        const numMatch = v.match(/[_.\s-]+(\d+)(?:\.[a-z]{2})?$/);
        const tapeNum = tapeMatch ? parseInt(tapeMatch[1], 10) : (numMatch ? parseInt(numMatch[1], 10) : 1);
        tapeMap[r].add(tapeNum);
      }
    });
    const rootTapes = {};
    for (const [r, s] of Object.entries(tapeMap)) {
      rootTapes[r] = [...s].sort((a, b) => a - b);
    }
    return { allRoots: roots.sort(), rootTapes };
  }, [videoList]);

  const loadSubjectsForUser = React.useCallback((userId) => {
    axiosAuth.get(`/admin/user-subjects/${userId}`)
      .then(r => {
        // Support old format (array of strings) and new format (array of objects)
        const data = (r.data || []).map(a => typeof a === 'string' ? { project_root: a } : a);
        setUserSubjects(prev => ({ ...prev, [userId]: data }));
      })
      .catch(() => {});
  }, [axiosAuth]);

  // These controls decide which testimonies a person can see, so a rejected
  // save must never look like it worked. Previously the error was swallowed:
  // the pill simply never lit up and an admin could believe access had been
  // granted or revoked when nothing persisted.
  const saveSubjectsForUser = (userId, assignments) => {
    setSubjectSaving(prev => ({ ...prev, [userId]: true }));
    setSubjectError(prev => ({ ...prev, [userId]: '' }));
    axiosAuth.put(`/admin/user-subjects/${userId}`, { assignments })
      .then(() => setUserSubjects(prev => ({ ...prev, [userId]: assignments })))
      .catch(err => setSubjectError(prev => ({
        ...prev,
        [userId]: `Access change NOT saved: ${err?.response?.data?.detail || err.message}`,
      })))
      .finally(() => setSubjectSaving(prev => ({ ...prev, [userId]: false })));
  };
  const [loading, setLoading]       = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser]       = useState({ email: '', display_name: '', password: '', role: 'editor' });
  const [addError, setAddError]     = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [pwForm, setPwForm]         = useState({ current: '', next: '', confirm: '' });
  const [pwError, setPwError]       = useState('');
  const [pwOk, setPwOk]             = useState(false);
  const [pwLoading, setPwLoading]   = useState(false);

  // Per-user inline edit (display_name + email)
  const [editingUserId, setEditingUserId]   = useState(null);
  const [editUserForm, setEditUserForm]     = useState({ display_name: '', email: '' });
  const [editUserSaving, setEditUserSaving] = useState(false);
  const [editUserError, setEditUserError]   = useState('');

  // Per-user admin reset-password
  const [resetPwUserId, setResetPwUserId]   = useState(null);
  const [resetPwVal, setResetPwVal]         = useState('');
  const [resetPwSaving, setResetPwSaving]   = useState(false);
  const [resetPwError, setResetPwError]     = useState('');
  const [resetPwOk, setResetPwOk]           = useState(false);

  const startEditUser = (u) => {
    setEditingUserId(u.id);
    setEditUserForm({ display_name: u.display_name || '', email: u.email || '' });
    setEditUserError('');
    setResetPwUserId(null);
  };

  const saveEditUser = async (userId) => {
    setEditUserSaving(true); setEditUserError('');
    try {
      await axiosAuth.put(`/admin/users/${userId}`, editUserForm);
      setEditingUserId(null);
      loadUsers();
    } catch (err) {
      setEditUserError(err?.response?.data?.detail || 'Save failed');
    } finally { setEditUserSaving(false); }
  };

  const startResetPw = (u) => {
    setResetPwUserId(u.id);
    setResetPwVal('');
    setResetPwError('');
    setResetPwOk(false);
    setEditingUserId(null);
  };

  const doResetPw = async (userId) => {
    if (resetPwVal.length < 8) { setResetPwError('Must be 8+ characters'); return; }
    setResetPwSaving(true); setResetPwError(''); setResetPwOk(false);
    try {
      await axiosAuth.post(`/admin/users/${userId}/reset-password`, { new_password: resetPwVal });
      setResetPwOk(true);
      setTimeout(() => { setResetPwUserId(null); setResetPwOk(false); }, 1800);
    } catch (err) {
      setResetPwError(err?.response?.data?.detail || 'Reset failed');
    } finally { setResetPwSaving(false); }
  };

  const isAdmin = currentUser?.role === 'admin';
  const [customRoles, setCustomRoles] = useState([]);
  // Distinct from "loaded and empty": an admin seeing "No users found." after a
  // failed request would reasonably conclude the user table was wiped.
  const [usersError, setUsersError] = useState(false);

  const loadUsers = React.useCallback(() => {
    if (!isAdmin) return;
    setLoading(true);
    axiosAuth.get('/admin/users')
      .then(r => { setUsers(r.data); setUsersError(false); })
      .catch(() => setUsersError(true))
      .finally(() => setLoading(false));
    axiosAuth.get('/admin/roles')
      .then(r => setCustomRoles(r.data || []))
      .catch(() => {});
  }, [isAdmin, axiosAuth]);

  React.useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setAddError(''); setAddLoading(true);
    try {
      await axiosAuth.post('/admin/users', newUser);
      setNewUser({ email: '', display_name: '', password: '', role: 'editor' });
      setShowAddForm(false);
      loadUsers();
    } catch (err) {
      setAddError(err?.response?.data?.detail || 'Failed to create user');
    } finally { setAddLoading(false); }
  };

  // Keyed by user id like subjectError — a single shared string would show the
  // failure on the wrong row.
  const toggleActive = async (user) => {
    setToggleError(prev => ({ ...prev, [user.id]: '' }));
    try {
      await axiosAuth.put(`/admin/users/${user.id}`, { is_active: !user.is_active });
      loadUsers();
    } catch (e) {
      setToggleError(prev => ({
        ...prev,
        [user.id]: `${user.is_active ? 'Deactivate' : 'Re-activate'} failed: ${e?.response?.data?.detail || e.message || 'could not update user'}`,
      }));
    }
  };

  const changeRole = async (user, role) => {
    try {
      await axiosAuth.put(`/admin/users/${user.id}`, { role });
      loadUsers();
    } catch (e) {
      pushToast('user-role-change-error', 'error', e?.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleChangePw = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) { setPwError('Passwords do not match'); return; }
    if (pwForm.next.length < 8) { setPwError('Password must be at least 8 characters'); return; }
    setPwError(''); setPwOk(false); setPwLoading(true);
    try {
      await axiosAuth.post('/auth/change-password', { current_password: pwForm.current, new_password: pwForm.next });
      setPwOk(true); setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) { setPwError(err?.response?.data?.detail || 'Failed'); }
    finally { setPwLoading(false); }
  };

  const roleColors = useMemo(() => {
    const base = { admin: '#f59e0b', editor: '#007aff', viewer: '#34c759' };
    customRoles.forEach(cr => { base[cr.name] = cr.color || '#6b7280'; });
    return base;
  }, [customRoles]);

  return (
    <div style={{ overflowY: 'auto', flex: 1 }}>

      {/* My Account */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '0.68rem', fontWeight: 'bold', color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>My Account</div>
        <div style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#007aff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon icon={currentUser?.role === 'admin' ? Shield : User} size={17} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 'bold', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.display_name || currentUser?.email?.split('@')[0] || '—'}</div>
            <div style={{ fontSize: '0.75rem', color: theme.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.email}</div>
          </div>
          <span style={{ padding: '2px 9px', borderRadius: '10px', background: `${roleColors[currentUser?.role] || '#888'}22`, color: roleColors[currentUser?.role] || '#888', fontSize: '0.68rem', fontWeight: 'bold', textTransform: 'uppercase', flexShrink: 0 }}>{currentUser?.role}</span>
        </div>
      </div>

      {/* Change password (local accounts only) */}
      {currentUser?.auth_provider !== 'google' && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '0.68rem', fontWeight: 'bold', color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>Change Password</div>
          <form onSubmit={handleChangePw} style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {['current', 'next', 'confirm'].map(f => (
              <Input key={f} theme={theme} type="password" placeholder={f === 'current' ? 'Current password' : f === 'next' ? 'New password (8+ chars)' : 'Confirm new password'}
                value={pwForm[f]} onChange={e => setPwForm(p => ({ ...p, [f]: e.target.value }))} required />
            ))}
            {pwError && <div style={{ fontSize: '0.75rem', color: theme.danger }}>{pwError}</div>}
            {pwOk   && <div style={{ fontSize: '0.75rem', color: theme.success }}>Password changed successfully.</div>}
            <Button type="submit" variant="primary" fullWidth loading={pwLoading} icon={KeyRound} theme={theme}>
              Update Password
            </Button>
          </form>
        </div>
      )}

      {/* Admin: user list */}
      {isAdmin && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 'bold', color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.06em' }}>All Users</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="secondary" size="sm" icon={RefreshCw} onClick={loadUsers} theme={theme}>Refresh</Button>
              <Button variant="secondary" size="sm" active={showAddForm} icon={UserPlus} onClick={() => setShowAddForm(v => !v)} theme={theme}>Add User</Button>
            </div>
          </div>

          {showAddForm && (
            <form onSubmit={handleAddUser} style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <Input theme={theme} type="email" placeholder="Email *" required value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
                <Input theme={theme} type="text" placeholder="Display name *" required value={newUser.display_name} onChange={e => setNewUser(p => ({ ...p, display_name: e.target.value }))} />
                <Input theme={theme} type="password" placeholder="Password (8+ chars) *" required minLength={8} value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} />
                <Select theme={theme} value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}>
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  {customRoles.map(cr => (
                    <option key={cr.name} value={cr.name}>{cr.display_name}</option>
                  ))}
                  <option value="admin">Admin</option>
                </Select>
              </div>
              {addError && <div style={{ fontSize: '0.75rem', color: theme.danger }}>{addError}</div>}
              <Button type="submit" variant="primary" fullWidth loading={addLoading} icon={UserPlus} theme={theme}>
                Create Account
              </Button>
            </form>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub }}><Icon icon={Loader} size={18} style={{ animation: 'spin 1s linear infinite' }} /></div>
          ) : users && users.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[...users].sort((a, b) => (a.display_name || a.email || '').localeCompare(b.display_name || b.email || '')).map(u => {
                const isMe = String(u.id) === String(currentUser?.sub) || String(u.id) === String(currentUser?.id);
                const lastLogin = u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('sv-SE') : 'Never';
                const isEditingThis = editingUserId === u.id;
                const isResetPwThis = resetPwUserId === u.id;
                const toggleErr = toggleError[u.id];
                const hasDropdown = isEditingThis || isResetPwThis || expandedUserId === u.id || !!toggleErr;
                return (
                  <React.Fragment key={u.id}>
                  <div style={{ background: theme.card, border: `1px solid ${isMe ? '#007aff' : theme.border}`, borderRadius: hasDropdown ? '8px 8px 0 0' : '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px', opacity: u.is_active ? 1 : 0.5, borderBottom: hasDropdown ? 'none' : undefined }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: u.is_active ? (roleColors[u.role] || '#888') : '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon icon={u.auth_provider === 'google' ? Mail : User} size={13} color="#fff" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.display_name} {isMe && <span style={{ color: '#007aff', fontSize: '0.65rem' }}>(you)</span>}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: theme.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email} · Last login: {lastLogin}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                      {/* Edit contact info */}
                      <button onClick={() => isEditingThis ? setEditingUserId(null) : startEditUser(u)} title="Edit name / email"
                        style={{ padding: '3px 7px', borderRadius: '5px', border: `1px solid ${isEditingThis ? theme.glossaryAccent : theme.border}`, background: isEditingThis ? `${theme.glossaryAccent}18` : theme.card, color: isEditingThis ? theme.glossaryAccent : theme.textSub, fontSize: '0.68rem', cursor: 'pointer' }}>
                        <Icon icon={Pencil} size={10} />
                      </button>
                      {/* Reset password (local accounts only) */}
                      {u.auth_provider !== 'google' && (
                        <button onClick={() => isResetPwThis ? setResetPwUserId(null) : startResetPw(u)} title="Reset password"
                          style={{ padding: '3px 7px', borderRadius: '5px', border: `1px solid ${isResetPwThis ? '#f59e0b' : theme.border}`, background: isResetPwThis ? '#f59e0b18' : theme.card, color: isResetPwThis ? '#f59e0b' : theme.textSub, fontSize: '0.68rem', cursor: 'pointer' }}>
                          <Icon icon={KeyRound} size={10} />
                        </button>
                      )}
                      <select value={u.role} disabled={isMe} onChange={e => changeRole(u, e.target.value)}
                        style={{ padding: '3px 6px', borderRadius: '5px', border: `1px solid ${theme.border}`, background: `${roleColors[u.role]}22`, color: roleColors[u.role] || theme.text, fontSize: '0.68rem', fontWeight: 'bold', cursor: isMe ? 'default' : 'pointer' }}>
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                        {customRoles.map(cr => (
                          <option key={cr.name} value={cr.name}>{cr.display_name}</option>
                        ))}
                        <option value="admin">Admin</option>
                      </select>
                      {!isMe && (
                        <button onClick={() => toggleActive(u)} title={u.is_active ? 'Deactivate' : 'Re-activate'}
                          style={{ padding: '3px 7px', borderRadius: '5px', border: `1px solid ${u.is_active ? '#ef444455' : '#22c55e55'}`, background: u.is_active ? '#ef444422' : '#22c55e22', color: u.is_active ? '#ef4444' : '#22c55e', fontSize: '0.68rem', fontWeight: 'bold', cursor: 'pointer' }}>
                          {u.is_active ? 'Disable' : 'Enable'}
                        </button>
                      )}
                      {isAdmin && allRoots.length > 0 && (
                        <button
                          onClick={() => {
                            if (expandedUserId === u.id) { setExpandedUserId(null); }
                            else { setExpandedUserId(u.id); loadSubjectsForUser(u.id); }
                          }}
                          title="Assign subjects"
                          style={{ padding: '3px 7px', borderRadius: '5px', border: `1px solid ${theme.border}`, background: expandedUserId === u.id ? theme.glossaryAccent : theme.card, color: expandedUserId === u.id ? '#fff' : theme.textSub, fontSize: '0.68rem', cursor: 'pointer' }}>
                          Subjects
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Enable/Disable failure — inline, matching the other error panels */}
                  {toggleErr && (
                    <div style={{ padding: '8px 12px 8px 50px', background: theme.bg,
                                  borderRadius: isEditingThis || isResetPwThis || expandedUserId === u.id ? '0' : '0 0 8px 8px',
                                  border: `1px solid ${isMe ? '#007aff' : theme.border}`, borderTop: '1px dashed #ef444444',
                                  display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ flex: 1, fontSize: '0.72rem', color: theme.danger, fontWeight: 600 }}>{toggleErr}</span>
                      <Button variant="secondary" size="sm" onClick={() => setToggleError(prev => ({ ...prev, [u.id]: '' }))} theme={theme} style={{ flexShrink: 0 }}>
                        Dismiss
                      </Button>
                    </div>
                  )}

                  {/* Inline edit contact info */}
                  {isEditingThis && (
                    <div style={{ padding: '10px 12px 10px 50px', background: theme.bg, borderRadius: isResetPwThis || expandedUserId === u.id ? '0' : '0 0 8px 8px', border: `1px solid ${isMe ? '#007aff' : theme.border}`, borderTop: `1px dashed ${theme.glossaryAccent}44`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Edit Contact Info</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                        <Input theme={theme} value={editUserForm.display_name} onChange={e => setEditUserForm(p => ({ ...p, display_name: e.target.value }))}
                          placeholder="Display name" />
                        <Input theme={theme} value={editUserForm.email} onChange={e => setEditUserForm(p => ({ ...p, email: e.target.value }))}
                          placeholder="Email" type="email" />
                      </div>
                      {editUserError && <div style={{ fontSize: '0.72rem', color: theme.danger }}>{editUserError}</div>}
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => saveEditUser(u.id)} disabled={editUserSaving}
                          style={{ padding: '5px 12px', borderRadius: '5px', background: theme.glossaryAccent, color: '#fff', border: 'none', fontWeight: 'bold', fontSize: '0.72rem', cursor: 'pointer' }}>
                          {editUserSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button onClick={() => setEditingUserId(null)}
                          style={{ padding: '5px 12px', borderRadius: '5px', background: theme.card, color: theme.textSub, border: `1px solid ${theme.border}`, fontSize: '0.72rem', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Inline reset password */}
                  {isResetPwThis && (
                    <div style={{ padding: '10px 12px 10px 50px', background: theme.bg, borderRadius: expandedUserId === u.id ? '0' : '0 0 8px 8px', border: `1px solid ${isMe ? '#007aff' : theme.border}`, borderTop: `1px dashed #f59e0b44`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reset Password</div>
                      <Input theme={theme} value={resetPwVal} onChange={e => setResetPwVal(e.target.value)} type="password"
                        placeholder="New password (8+ chars)" />
                      {resetPwError && <div style={{ fontSize: '0.72rem', color: theme.danger }}>{resetPwError}</div>}
                      {resetPwOk   && <div style={{ fontSize: '0.72rem', color: theme.success }}>Password reset successfully.</div>}
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => doResetPw(u.id)} disabled={resetPwSaving}
                          style={{ padding: '5px 12px', borderRadius: '5px', background: '#f59e0b', color: '#fff', border: 'none', fontWeight: 'bold', fontSize: '0.72rem', cursor: 'pointer' }}>
                          {resetPwSaving ? 'Resetting…' : 'Reset Password'}
                        </button>
                        <button onClick={() => setResetPwUserId(null)}
                          style={{ padding: '5px 12px', borderRadius: '5px', background: theme.card, color: theme.textSub, border: `1px solid ${theme.border}`, fontSize: '0.72rem', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Subject assignment panel */}
                  {expandedUserId === u.id && isAdmin && (
                    <div style={{ padding: '10px 12px 10px 50px', background: theme.bg, borderRadius: '0 0 8px 8px', border: `1px solid ${isMe ? '#007aff' : theme.border}`, borderTop: `1px dashed ${theme.border}` }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, color: theme.textSub, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Assigned Subjects — Access Mode:
                        <Select
                          theme={theme}
                          size="sm"
                          fullWidth={false}
                          value={u.subject_access || 'all_starred'}
                          style={{ marginLeft: '8px' }}
                          onChange={e => {
                            setSubjectError(prev => ({ ...prev, [u.id]: '' }));
                            axiosAuth.put(`/admin/users/${u.id}`, { subject_access: e.target.value })
                              .then(() => loadUsers())
                              .catch(err => setSubjectError(prev => ({
                                ...prev,
                                [u.id]: `Access mode NOT saved: ${err?.response?.data?.detail || err.message}`,
                              })));
                          }}>
                          <option value="all_starred">All subjects, assigned starred</option>
                          <option value="assigned_only">Assigned subjects only</option>
                          <option value="all_default_assigned">All, default to assigned</option>
                        </Select>
                      </div>
                      {/* Subject pills — click to toggle assignment */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px' }}>
                        {allRoots.map(root => {
                          const assignments = userSubjects[u.id] || [];
                          const assignment = assignments.find(a => a.project_root === root);
                          const assigned = !!assignment;
                          return (
                            <button key={root}
                              onClick={() => {
                                const cur = assignments;
                                const next = assigned
                                  ? cur.filter(a => a.project_root !== root)
                                  : [...cur, { project_root: root, role_override: null, allowed_parts: null }];
                                saveSubjectsForUser(u.id, next);
                              }}
                              style={{ padding: '3px 8px', borderRadius: '12px', border: `1px solid ${assigned ? theme.glossaryAccent : theme.border}`, background: assigned ? `${theme.glossaryAccent}18` : theme.card, color: assigned ? theme.glossaryAccent : theme.textSub, fontSize: '0.68rem', cursor: 'pointer', fontWeight: assigned ? 600 : 400 }}>
                              {assigned && <Icon icon={Star} size={10} style={{ marginRight: '4px' }} />}
                              {root.replace(/_/g,' ')}
                              {assignment?.role_override && <span style={{ marginLeft: '4px', opacity: 0.7 }}>({assignment.role_override})</span>}
                              {assignment?.allowed_parts && <span style={{ marginLeft: '4px', opacity: 0.7 }}>T{assignment.allowed_parts.join(',')}</span>}
                            </button>
                          );
                        })}
                      </div>
                      {/* Per-subject granular settings for assigned subjects */}
                      {(() => {
                        const assignments = userSubjects[u.id] || [];
                        const assigned = assignments.filter(a => a.project_root);
                        if (assigned.length === 0) return null;
                        const roleColors = { editor: '#007aff', viewer: '#34c759', admin: '#f59e0b' };
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '8px' }}>
                            <div style={{ fontSize: '0.65rem', fontWeight: 700, color: theme.textSub, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Per-Subject Permissions</div>
                            {assigned.map(a => {
                              const tapes = rootTapes[a.project_root] || [];
                              return (
                                <div key={a.project_root} style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '6px', padding: '6px 10px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: tapes.length > 1 ? '4px' : 0 }}>
                                    <span style={{ fontSize: '0.72rem', fontWeight: 600, flex: 1 }}>{a.project_root.replace(/_/g,' ')}</span>
                                    <select
                                      value={a.role_override || ''}
                                      style={{ padding: '2px 5px', borderRadius: '4px', border: `1px solid ${theme.border}`, background: a.role_override ? `${roleColors[a.role_override] || '#888'}18` : theme.inputBg, color: a.role_override ? (roleColors[a.role_override] || '#888') : theme.textSub, fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer' }}
                                      onChange={e => {
                                        const val = e.target.value || null;
                                        const next = assignments.map(x => x.project_root === a.project_root ? { ...x, role_override: val } : x);
                                        saveSubjectsForUser(u.id, next);
                                      }}>
                                      <option value="">Use global role ({u.role})</option>
                                      <option value="viewer">Viewer</option>
                                      <option value="editor">Editor</option>
                                      {customRoles.map(cr => (
                                        <option key={cr.name} value={cr.name}>{cr.display_name}</option>
                                      ))}
                                    </select>
                                  </div>
                                  {tapes.length > 1 && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: '0.62rem', color: theme.textSub, marginRight: '2px' }}>Tapes:</span>
                                      <button
                                        onClick={() => {
                                          const next = assignments.map(x => x.project_root === a.project_root ? { ...x, allowed_parts: null } : x);
                                          saveSubjectsForUser(u.id, next);
                                        }}
                                        style={{ padding: '1px 6px', borderRadius: '8px', border: `1px solid ${!a.allowed_parts ? theme.glossaryAccent : theme.border}`, background: !a.allowed_parts ? `${theme.glossaryAccent}18` : theme.card, color: !a.allowed_parts ? theme.glossaryAccent : theme.textSub, fontSize: '0.6rem', cursor: 'pointer', fontWeight: !a.allowed_parts ? 600 : 400 }}>
                                        All
                                      </button>
                                      {tapes.map(t => {
                                        const selected = a.allowed_parts ? a.allowed_parts.includes(t) : true;
                                        const isRestricted = !!a.allowed_parts;
                                        return (
                                          <button key={t}
                                            onClick={() => {
                                              let newParts;
                                              if (!a.allowed_parts) {
                                                // Currently "all" — switch to selecting specific tapes (all except this one)
                                                newParts = tapes.filter(x => x !== t);
                                              } else if (selected) {
                                                newParts = a.allowed_parts.filter(x => x !== t);
                                                if (newParts.length === 0) newParts = null; // back to all
                                              } else {
                                                newParts = [...a.allowed_parts, t].sort((a, b) => a - b);
                                                if (newParts.length === tapes.length) newParts = null; // all selected = null
                                              }
                                              const next = assignments.map(x => x.project_root === a.project_root ? { ...x, allowed_parts: newParts } : x);
                                              saveSubjectsForUser(u.id, next);
                                            }}
                                            style={{ padding: '1px 6px', borderRadius: '8px', border: `1px solid ${isRestricted && selected ? '#007aff' : isRestricted && !selected ? '#e5091444' : theme.border}`, background: isRestricted && selected ? '#007aff18' : isRestricted && !selected ? '#e5091408' : theme.card, color: isRestricted && selected ? '#007aff' : isRestricted && !selected ? '#e50914' : theme.textSub, fontSize: '0.6rem', cursor: 'pointer', fontWeight: isRestricted && selected ? 600 : 400, textDecoration: isRestricted && !selected ? 'line-through' : 'none' }}>
                                            {t}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {subjectSaving[u.id] && <div style={{ fontSize: '0.65rem', color: theme.textSub }}>Saving…</div>}
                      {subjectError[u.id] && (
                        <div style={{ fontSize: '0.7rem', color: theme.danger, fontWeight: 600, marginTop: '4px',
                                      background: '#ef444414', border: '1px solid #ef444444',
                                      borderRadius: '6px', padding: '5px 8px' }}>
                          {subjectError[u.id]}
                        </div>
                      )}
                    </div>
                  )}
                  </React.Fragment>
                );
              })}
            </div>
          ) : (
            usersError
              ? <LoadFailure theme={theme} what="the user list" onRetry={loadUsers} />
              : <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub, fontSize: '0.85rem' }}>No users found.</div>
          )}
        </div>
      )}

      {!isAdmin && (
        <div style={{ fontSize: '0.78rem', color: theme.textSub, textAlign: 'center', padding: '20px 0' }}>
          Contact your administrator to manage users.
        </div>
      )}
    </div>
  );
}
